import { type ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { Button } from '@repo/design-system-kit/components/button/button';
import { Checkbox } from '@repo/design-system-kit/components/checkbox/checkbox';
import { List } from '@repo/design-system-kit/components/list/list';
import { LottiePlayer } from '@repo/design-system-kit/components/lottie-player/lottie-player';
import { WarningSign } from '@repo/design-system-kit/assets/icons/warning-sign';
import { useSegment } from '@repo/poly-hooks/segment';
import { TNCLink } from '@repo/shared-kit/components/tnc-link/tnc-link';
import kycFourthlineLottie from '@repo/visual-assets/dotlottie/lottie_cp_onboarding_kyc_fourthline.lottie';
import { BackButtonSimple } from '../../shared/back-button-simple';
import styles from './reassurance.module.css';

export interface ReassuranceProps {
  isKycRequired: boolean;
  isQesRequired: boolean;
  isKycExpired?: boolean;
  mode: string;
  onContinue: () => void;
  legalCountry: string;
  onBack?: () => void;
}

export function Reassurance({
  isKycRequired,
  isQesRequired,
  isKycExpired,
  legalCountry,
  onContinue,
  onBack,
  mode,
}: ReassuranceProps): ReactNode {
  const [checked, setChecked] = useState(false);
  const [showError, setShowError] = useState(false);
  const segment = useSegment();
  const intl = useIntl();
  const fourthlineWebsiteHref = intl.formatMessage({
    id: 'identity-verification-kit.links.fourthlineWebsite',
  });
  const fourthlineTermsHref = intl.formatMessage({
    id: 'identity-verification-kit.links.fourthlineTerms',
  });

  const { titleId, subtitleId } = (() => {
    if (isKycExpired) {
      return {
        titleId: 'identity-verification-kit.fourthline.reassurance.kyc.renew.title',
        subtitleId: 'identity-verification-kit.fourthline.reassurance.kyc.renew.subtitle',
      };
    }

    if (isKycRequired && isQesRequired) {
      return {
        titleId: 'identity-verification-kit.fourthline.reassurance.kyc-and-qes.title',
        subtitleId: 'identity-verification-kit.fourthline.reassurance.kyc-and-qes.subtitle',
      };
    }

    if (isKycRequired) {
      return {
        titleId: 'identity-verification-kit.fourthline.reassurance.kyc.title',
        subtitleId: 'identity-verification-kit.fourthline.reassurance.kyc.subtitle',
      };
    }

    return {
      titleId: 'identity-verification-kit.fourthline.reassurance.qes.title',
      subtitleId: 'identity-verification-kit.fourthline.reassurance.qes.subtitle',
    };
  })();

  const handleSubmit = (): void => {
    if (!checked) {
      setShowError(true);
      return;
    }
    onContinue();
  };

  const bulletPointsList = (() => {
    const list = [
      intl.formatMessage({
        id: 'identity-verification-kit.fourthline.reassurance.requirement.network',
      }),
      intl.formatMessage({
        id: 'identity-verification-kit.fourthline.reassurance.requirement.phone',
      }),
    ];

    if (isKycExpired) {
      return [
        intl.formatMessage({ id: 'identity-verification-kit.fourthline.reassurance.kyc.renew.id' }),
        ...list,
      ];
    }

    if (isKycRequired) {
      return [
        intl.formatMessage(
          { id: 'identity-verification-kit.fourthline.reassurance.requirement.id' },
          { legalCountry }
        ),
        ...list,
      ];
    }

    return list;
  })();

  /*
   * WARNING: This component is shared across multile apps and packages.
   * Changes here will affect all consumers.
   * Make sure to align with @qonto-private/team-web-due-diligence before applying them.
   */
  return (
    <div data-test-reassurance>
      {onBack ? <BackButtonSimple className="mb-32" data-test-go-back onPress={onBack} /> : null}

      <LottiePlayer autoplay className={styles.illustration} loop src={kycFourthlineLottie} />

      <header className={styles.header}>
        <h2 className="heading-section">
          {/* oxlint-disable-next-line @repo/no-dynamic-intl-id */}
          <FormattedMessage id={titleId} />
        </h2>
        <p className="body-standard" data-test-agreement-subtitle>
          <FormattedMessage
            // oxlint-disable-next-line @repo/no-dynamic-intl-id
            id={subtitleId}
            values={{
              fourthlineLink: (chunks: ReactNode) => (
                <a
                  className="body-standard-link"
                  href={fourthlineWebsiteHref}
                  onClick={() => {
                    segment.track(`${mode}_fourthline_website_link_clicked`);
                  }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </p>
      </header>
      <div className={cx('mb-24', styles.bulletPointsList)} data-test-bulletpoints>
        <List
          items={bulletPointsList.map((message, index) => ({
            icon: 'check' as const,
            key: `bullet-${index}`,
            text: message,
          }))}
        />
      </div>
      {/* there's an ongoing issue with checkbox validation is not removing the error when it should but a fix is ready here: https://github.com/adobe/react-spectrum/pull/6079 */}
      <div className="mb-32">
        <div className={cx('body-standard mb-16', styles.disclaimer)} data-testid="disclaimer">
          <FormattedMessage
            id="identity-verification-kit.fourthline.reassurance.terms.label"
            values={{
              fourthlineLink: (chunks: ReactNode) => (
                <a
                  className="body-standard-link"
                  href={fourthlineTermsHref}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {chunks}
                </a>
              ),
              qontoPrivacyPolicyLink: (chunks: ReactNode) => (
                <TNCLink
                  className="body-standard-link"
                  language={intl.locale}
                  tncKey="qonto-privacy-policy"
                >
                  {chunks}
                </TNCLink>
              ),
            }}
          />
        </div>
        <label
          className={styles.checkboxContainer}
          htmlFor="fourthline-terms-privacy-policy-checkbox"
        >
          <Checkbox
            aria-label={intl.formatMessage({
              id: 'identity-verification-kit.fourthline.reassurance.terms.consent',
            })}
            data-test-agreement-checkbox
            id="fourthline-terms-privacy-policy-checkbox"
            onChange={setChecked}
          />
          <span className={cx('body-standard', styles.label)} data-testid="label">
            <FormattedMessage id="identity-verification-kit.fourthline.reassurance.terms.consent" />
          </span>
        </label>
        {!checked && showError ? (
          <div className={styles.error} data-test-error-label="" role="alert">
            <WarningSign className="mr-8" />
            <FormattedMessage id="identity-verification-kit.fourthline.reassurance.terms.error" />
          </div>
        ) : null}
      </div>
      <Button data-test-agreement-submit onPress={handleSubmit} type="submit">
        <FormattedMessage id="identity-verification-kit.btn.continue" />
      </Button>
    </div>
  );
}
